import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import DataTable from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import {LanguagesResponse, Language} from "../../../types/Responses/LanguagesResponse";
import StatusLabel from "../../../components/StatusLabel";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {useToastContext} from "../../../types/contexts/ToastContext";
import CountryAvatar from "../../../components/CountryAvatar";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {setSidebarTitle} from "../../../reducers/sidebar";
import {useDispatch} from "react-redux";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import FilterChips, {FilterChipDefinition} from "../../../components/FilterChips";
import DropdownInput from "../../../components/DropdownInput";

type LanguagesDataTableRequest = DataTableRequest & { status?: number };

const Languages = () => {
    const {t} = useTranslation();
    const user = useUserContext();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const system = useSystemContext();
    const handleError = useErrorHandler();
    const toast = useToastContext();
    const [languages, setLanguages] = useState<LanguagesResponse>();
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const defaultRequest = useMemo<LanguagesDataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<LanguagesDataTableRequest>(defaultRequest);
    const status = dataTableRequest.status ?? 0;

    const sendRequest = useCallback((request: LanguagesDataTableRequest) => {
        setIsDataTableLoading(true);
        const payload = {...request};
        if (!payload.status || payload.status <= 0) {
            delete payload.status;
        }
        API.getLanguages(payload).then((response: AxiosResponse) => {
            const languagesResponse = response.data as LanguagesResponse;
            setLanguages(languagesResponse);
            setIsDataTableLoading(false);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_LANGUAGES}`, toast, handleError, setIsForbidden));
    }, [toast, handleError]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        const updatedRequest = {...request, status};
        persistDataTableRequest(updatedRequest);
        if (!request.search || request.search.length > 3) {
            const normalizedRequest = !request.search ? {...updatedRequest, search: ''} : updatedRequest;
            sendRequest(normalizedRequest);
        }
    }, [persistDataTableRequest, sendRequest, status]);

    const onStatusChanged = useCallback((s: string | number) => {
        const st = typeof s === 'string' ? parseInt(s) : s;
        const request = {...dataTableRequest, status: st, page: 1};
        persistDataTableRequest(request);
        sendRequest(request);
    }, [dataTableRequest, sendRequest, persistDataTableRequest]);

    const deleteLanguage = useCallback((language: Language) => {
        if (languages) {
            setIsDataTableLoading(true);
            const filteredArray = {
                ...languages,
                data: languages.data.filter(r => r.id !== language.id),
                count: languages.count - 1
            };
            API.deleteLanguage(language.id).then(() => {
                if (language) {
                    setLanguages(filteredArray)
                    setIsDataTableLoading(false);
                }
            }).catch(e => ErrorHandler(e, `delete${APILink.GET_LANGUAGE.replace('$language', language.id?.toString() ?? '')}`, toast, handleError));
        }
    }, [setIsDataTableLoading, setLanguages, languages, toast, handleError]);

    useEffect(() => {
        setTitle(t('admin.languages.title'));
        dispatch(setSidebarTitle(t('admin.languages.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const request = {...dataTableRequest, search: newID[1]};
                        persistDataTableRequest(request);
                        sendRequest(request);
                    }
                } else {
                    sendRequest(dataTableRequest);
                }
            }).catch();
        }
    }, [t, user, dispatch, setSidebarTitle]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);


    const filterChipsItems = useMemo<FilterChipDefinition[]>(() => {
        if (!system?.system?.statuses || status <= 0) {
            return [];
        }
        const selectedStatus = system.system.statuses.find((st) => st.id === status);
        if (!selectedStatus) {
            return [];
        }
        return [{
            key: `status-${selectedStatus.id}`,
            label: `${t('admin.languages.table.status')}: ${t(selectedStatus.name)}`,
            onRemove: () => onStatusChanged('0')
        }];
    }, [system, status, t, onStatusChanged]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('create language') &&
                    <Button onClick={() => navigate('/app/admin/languages/create')}
                            className="bg-black text-white">{t('admin.languages.new.button')}</Button>}
            </div>
        )
    }, [user, refresh]);

    const dataTable = useMemo(() => {
        if (!languages || !system || !system.system)
            return <></>;

        return <DataTable {...dataTableRequest}
                          total_pages={languages.pages.total}
                          count={languages.count}
                          nodata={languages.data.length === 0}
                          inProgress={isDataTableLoading}
                          rightPanel={actionsPanel}
                          filters={
                              <DropdownInput
                                  withAllButton={true}
                                  name={t('admin.languages.table.status')}
                                  selected={status}
                                  onChange={onStatusChanged}
                                  onSelectAll={() => onStatusChanged('')}
                                  list={system.system.statuses}
                                  menuClassName="w-full -mt-5"
                              />
                          }
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChipsItems}/>}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t('admin.languages.table.name'), sortBy: 'name'},
            {name: t('admin.languages.table.code'), sortBy: 'code'},
            {name: t('admin.languages.table.status'), className: 'text-center', sortBy: 'status_id'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {languages.data.map((language, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD><CountryAvatar country={language} className="h-3 max-w-[2.5rem]"/></TD>
                        <TD>{language.code.toUpperCase()}</TD>
                        <TD className="text-center"><StatusLabel status={language.status}/></TD>
                        <TD className="text-right">
                            <>
                                {user?.user.permissions.includes('update language') &&
                                    <Button onClick={() => navigate(`/app/admin/languages/${language.id}`)}
                                            className="px-5 py-2 text-white bg-black lg:mr-2"><FontAwesomeIcon
                                        icon={solid("pen-to-square")}/></Button>}
                                {user?.user.permissions.includes('delete language') &&
                                    <Button onClick={() => deleteLanguage(language)}
                                            className="px-5 py-2 text-red-300 bg-black"><FontAwesomeIcon
                                        icon={solid("trash")}/></Button>}
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, system, isDataTableLoading, languages, status, filterChipsItems, deleteLanguage, navigate, onDataTableChanged, onStatusChanged, user?.user.permissions]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {dataTable}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Languages);
